import { ReactNode, ReactElement, useMemo, useEffect } from 'react';
import { usePreferences } from '@/common/providers/preferences-provider';
import { Company } from '@/api';
import { useCompanies } from '../companies-provider';
import { NullableCompanyContext, NullableCompanyContextData } from './context';

type NullableCompanyContextProviderProps = {
    readonly children?: ReactNode;
};

export function NullableCompanyContextProvider({
    children
}: NullableCompanyContextProviderProps): ReactElement {
    const { prefs, setPrefs, removePrefs } = usePreferences();

    const { companies } = useCompanies();

    const company = useMemo(() => {
        return checkAndSetContext(prefs.companyContext || null, companies);
    }, [prefs, companies]);

    useEffect(() => {
        if (company === null) {
            removePrefs('companyContext');
        } else if (company && company.id !== prefs.companyContext) {
            setPrefs('companyContext', company.id);
        }
    }, [prefs, company, setPrefs, removePrefs]);

    const value: NullableCompanyContextData = {
        company,
        setCompany: (companyId) => {
            setPrefs('companyContext', companyId);
        }
    };

    return (
        <NullableCompanyContext.Provider value={value}>
            {children}
        </NullableCompanyContext.Provider>
    );
}

function checkAndSetContext(
    companyContext: string | null,
    companies: Company[]
): Company | null {
    if (companyContext) {
        const foundCompany = companies.find(
            (company) => company.id === companyContext
        );
        if (foundCompany) {
            return foundCompany;
        } else {
            if (companies.length > 0) {
                return companies[0];
            } else {
                return null;
            }
        }
    } else {
        if (companies.length > 0) {
            return companies[0];
        } else {
            return null;
        }
    }
}
